import { useState } from 'react';
import AmbientBackground from '../common/AmbientBackground';
import Button from '../common/Button';
import { AuthService } from '../../services/authService';
import { validateEmail } from '../../utils/sanitize';

/**
 * 🔒 SECURE Login Screen — Hybrid Authentication
 * 
 * PRIMARY: Google Sign-In (OAuth2) — password never enters our app
 * FALLBACK: Email/Password — for non-Google accounts (existing Firebase Auth users)
 * 
 * Existing users continue to work with either method.
 * Firebase auto-links accounts with matching emails.
 */

const LoginScreen = ({ onLogin, onShowProspect }) => {
    const [showEmailLogin, setShowEmailLogin] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({ password: null, email: null, form: null });
    const [loading, setLoading] = useState(false);

    // ========================
    // GOOGLE SIGN-IN (PRIMARY)
    // ========================
    const handleGoogleLogin = async () => {
        setErrors({ password: null, email: null, form: null });
        setLoading(true);

        try {
            const firebaseUser = await AuthService.loginWithGoogle();
            if (!firebaseUser) {
                // Redirect flow was initiated (popup blocked)
                return;
            }
            // Auth observer in App.jsx handles routing
        } catch (error) {
            if (error.message === 'LOGIN_CANCELLED') {
                // User closed popup, not an error
                setErrors({ password: null, email: null, form: null });
            } else {
                let errorMessage = "Errore durante l'accesso Google. Riprova.";
                if (error.code === 'auth/unauthorized-domain') {
                    errorMessage = "Dominio non autorizzato. Contatta l'amministratore.";
                } else if (error.code === 'auth/network-request-failed') {
                    errorMessage = "Errore di rete. Controlla la connessione.";
                }
                setErrors({ password: null, email: null, form: errorMessage });
            }
        } finally {
            setLoading(false);
        }
    };

    // ====================================
    // EMAIL/PASSWORD LOGIN (LEGACY FALLBACK)
    // ====================================
    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setErrors({ password: null, email: null, form: null });

        // Validate email
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            setErrors(prev => ({ ...prev, email: emailValidation.error }));
            return;
        }

        const emailLower = emailValidation.sanitized.toLowerCase();
        const GOOGLE_COUNSELOR_EMAILS = [
            'patrizia.marchi.1969@gmail.com',
            'martina@gmail.com',
            'trebino.martina@gmail.com',
            'marzia.macca@gmail.com',
            'patrizia.marforio@gmail.com',
            'marilisa.delve@gmail.com'
        ];

        if (GOOGLE_COUNSELOR_EMAILS.includes(emailLower) || emailLower.endsWith('@gmail.com')) {
            setErrors(prev => ({
                ...prev,
                form: `L'account ${emailValidation.sanitized} accede tramite Google senza password. Clicca su "Accedi con Google".`
            }));
            setShowEmailLogin(false);
            return;
        }

        if (!password || password.length < 6) {
            setErrors(prev => ({ ...prev, password: "La password deve essere di almeno 6 caratteri" }));
            return;
        }

        setLoading(true);

        try {
            await AuthService.loginWithEmail(emailValidation.sanitized, password);
            // Clear password from memory immediately
            setPassword('');
            // Auth observer in App.jsx handles routing
        } catch (error) {
            console.error("Login Error:", error);
            let errorMessage = "Credenziali non valide.";
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                errorMessage = "Email o password errati.";
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = "Troppi tentativi. Riprova più tardi.";
            } else if (error.code === 'auth/invalid-credential') {
                errorMessage = "Credenziali non valide. Verifica email e password.";
            }
            setErrors(prev => ({ ...prev, form: errorMessage }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 animate-fade-in relative overflow-hidden">
            <AmbientBackground />

            <div className="glass-card w-full max-w-md p-8 rounded-3xl relative z-10">
                <div className="flex justify-center mb-4">
                    <img
                        src="/logo.png"
                        alt="AwarenessTool"
                        className="h-16 w-auto object-contain"
                    />
                </div>
                <h1 className="text-3xl font-light mb-2 text-center">AwarenessTool</h1>
                <p className="text-gray-500 text-center mb-2">
                    La tecnologia a supporto del counseling
                </p>

                {/* Security Badge */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
                        <span className="text-xs">🔒</span>
                        <span className="text-[10px] font-bold text-green-700 uppercase tracking-wider">
                            Autenticazione Sicura
                        </span>
                    </div>
                </div>

                <div className="space-y-4 min-h-[200px]">
                    {/* ==================== */}
                    {/* GOOGLE SIGN-IN (PRIMARY) */}
                    {/* ==================== */}
                    <button
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border-2 font-medium transition-all duration-300 transform active:scale-[0.98] ${loading
                            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400 hover:shadow-lg hover:shadow-gray-100'
                            }`}
                    >
                        {loading && !showEmailLogin ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                                <span>Accesso in corso...</span>
                            </>
                        ) : (
                            <>
                                {/* Google Logo SVG */}
                                <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                <span className="text-base">Accedi con Google</span>
                            </>
                        )}
                    </button>

                    <div className="flex items-center justify-center gap-1.5">
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 rounded text-[9px] font-bold text-green-600 uppercase tracking-wider">
                            🛡️ Consigliato — la password non transita dalla nostra app
                        </div>
                    </div>

                    {/* ==================== */}
                    {/* EMAIL/PASSWORD FALLBACK */}
                    {/* ==================== */}
                    {!showEmailLogin ? (
                        <div className="pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setShowEmailLogin(true)}
                                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition py-2"
                            >
                                Hai un account non Google? <span className="underline">Accedi con email e password</span>
                            </button>
                        </div>
                    ) : (
                        <div className="pt-4 border-t border-gray-100">
                            {/* Warning banner for email/password */}
                            <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                                <div className="flex items-start gap-2">
                                    <span className="text-sm">⚠️</span>
                                    <p className="text-[10px] text-amber-700 leading-relaxed">
                                        <strong>Login legacy:</strong> Se possibile, chiedi al tuo counselor di associare il tuo account a Google per una sicurezza migliore.
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handleEmailLogin} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        className={`w-full p-4 rounded-xl bg-white/50 border transition ${errors.email ? 'border-red-300 focus:ring-red-400' : 'border-white/30 focus:ring-blue-400'} focus:outline-none focus:ring-2`}
                                        placeholder="tua@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading}
                                        autoComplete="email"
                                    />
                                    {errors.email && <p className="text-red-600 text-xs mt-1">⚠️ {errors.email}</p>}
                                    {Boolean(email && (email.toLowerCase().includes('patrizia.marchi') || email.toLowerCase().endsWith('@gmail.com'))) && (
                                        <div className="mt-2 p-3 bg-sky-50 border border-sky-200 rounded-xl text-sky-900 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2 animate-fade-in">
                                            <div>
                                                <span className="font-bold">Account Google abilitato:</span>
                                                <p className="text-[11px] text-sky-700 mt-0.5">Non serve la password. Accedi direttamente con Google.</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleGoogleLogin}
                                                className="px-3 py-1.5 bg-[#1e3847] hover:bg-[#24485d] text-white rounded-lg text-xs font-bold transition shadow-xs shrink-0 cursor-pointer"
                                            >
                                                Accedi con Google
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-sm font-medium text-gray-700">Password</label>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                const emailVal = validateEmail(email);
                                                if (!emailVal.valid) {
                                                    setErrors(prev => ({ ...prev, email: "Inserisci la tua email per il reset" }));
                                                    return;
                                                }
                                                const emLower = emailVal.sanitized.toLowerCase();
                                                if (emLower === 'patrizia.marchi.1969@gmail.com' || emLower.endsWith('@gmail.com')) {
                                                    setErrors(prev => ({ ...prev, form: "Questo account accede con Google (OAuth). Non serve password: clicca su 'Accedi con Google'." }));
                                                    return;
                                                }
                                                try {
                                                    setLoading(true);
                                                    await AuthService.resetPassword(emailVal.sanitized);
                                                    setErrors(prev => ({ ...prev, form: "✅ Email di recupero inviata! Controlla la posta." }));
                                                } catch (err) {
                                                    setErrors(prev => ({ ...prev, form: "Errore. Verifica che l'email sia corretta." }));
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                        >
                                            Password dimenticata?
                                        </button>
                                    </div>
                                    <input
                                        type="password"
                                        className={`w-full p-4 rounded-xl bg-white/50 border transition ${errors.password ? 'border-red-300 focus:ring-red-400' : 'border-white/30 focus:ring-blue-400'} focus:outline-none focus:ring-2`}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        autoComplete="current-password"
                                    />
                                    {errors.password && <p className="text-red-600 text-xs mt-1">⚠️ {errors.password}</p>}
                                </div>

                                <Button className="w-full" disabled={loading}>
                                    {loading ? 'Accesso in corso...' : 'Accedi con Email'}
                                </Button>
                            </form>

                            <button
                                onClick={() => {
                                    setShowEmailLogin(false);
                                    setPassword('');
                                    setErrors({ password: null, email: null, form: null });
                                }}
                                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition py-2 mt-2"
                            >
                                ← Torna all'accesso Google
                            </button>
                        </div>
                    )}

                    {errors.form && (
                        <div className={`p-3 rounded-xl text-sm text-center border ${errors.form.startsWith('✅')
                            ? 'bg-green-50 text-green-600 border-green-100'
                            : 'bg-red-50 text-red-600 border-red-100'
                            }`}>
                            {errors.form}
                        </div>
                    )}
                </div>

                {/* Prospect/Landing Section */}
                <div className="mt-10 pt-8 border-t border-slate-100 text-center">
                    <p className="text-sm font-black text-slate-800 italic uppercase tracking-widest mb-4">
                        Sei un nuovo aspirante cliente?
                    </p>
                    <button 
                        onClick={onShowProspect}
                        className="w-full py-4 px-6 bg-[#335d73] text-white rounded-[2rem] font-black text-sm hover:opacity-90 transition transform hover:-translate-y-1 active:scale-[0.98] shadow-xl hover:shadow-2xl hover:shadow-[#85a8bf]/20"
                    >
                        Ti incuriosisce ma non sai se fa per te? <br/>
                        <span className="text-[10px] font-medium text-[#d9e7ed] normal-case">Esplora il percorso e la nostra filosofia</span>
                    </button>
                    <p className="mt-4 text-[10px] text-slate-400 italic">
                        📩 Oppure scrivici a <a href="mailto:info@awarenesstool.it" className="underline hover:text-[#85a8bf] transition">info@awarenesstool.it</a>
                    </p>
                </div>

                <p className="text-center text-[10px] text-gray-400 mt-6 uppercase tracking-widest font-black opacity-50">
                    AwarenessTool Framework
                </p>
            </div>
        </div>
    );
};

export default LoginScreen;
